<template>
    <div
        v-qa.component="'side-by-side-images'"
        class="side-by-side-images"
    >
        <div class="carousel">
            <div
                ref="container"
                class="slides"
            >
                <div
                    v-for="(image, key) in data.images"
                    :key="image.image.id"
                    ref="elements"
                    class="slide"
                >
                    <ComponentSideBySideImage
                        :key="image.image.id"
                        v-qa.slide="key"
                        :data="image"
                        :page-type="pageType"
                        :is-eager="hasEagerImage"
                        :sizes="[
                            'calc(100vw - 80px)',
                            [640, 'calc((100vw - 100px) / 2)'],
                            [1024, '321px'],
                        ]"
                    />
                </div>
            </div>
        </div>

        <ButtonRound
            v-qa="'previous'"
            :disabled="isAtStart"
            class="left"
            icon="angle-left"
            :aria-label="t('catalogue.carousel.button_prev')"
            @click="previous"
        />

        <ButtonRound
            v-qa="'next'"
            :disabled="isAtEnd"
            class="right"
            icon="angle-right"
            :aria-label="t('catalogue.carousel.button_next')"
            @click="next"
        />
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { useCarousel } from '@/components/layout/carousels/base/useCarouselNew'

import type { ComponentSideBySideImagesFragment } from '@/graphql/api'

import ComponentSideBySideImage from '@/components/cms/component/side-by-side-images/ComponentSideBySideImage.vue'
import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'
import type { PageType } from '@/components/cms/component/content/ContentButton.vue'

const { data, pageType } = defineProps<{
    data: ComponentSideBySideImagesFragment
    hasEagerImage?: boolean
    pageType: PageType
}>()

const { t } = useI18n()

const { previous, next, container, elements, isAtStart, isAtEnd } = useCarousel(
    computed(() => data.images.length)
)
</script>

<style lang="scss" scoped>
.side-by-side-images {
    width: 100%;
    max-width: 1024px;
    margin: 0 auto;
    position: relative;

    @include min-width(640) {
        display: flex;
        justify-content: space-between;
    }

    .carousel {
        width: 100%;
        position: relative;
        // TODO Alex V do we need the padding? Only makes slides smaller
        // But this must be on the parent not to affect .slides width

        .slides {
            --container-padding: 30px;
            position: relative;
            display: flex;
            overflow-y: hidden;
            overflow-x: scroll;
            scroll-snap-type: x mandatory;
            scroll-padding: 0 var(--container-padding);
            padding: 0 var(--container-padding);
            -ms-overflow-style: none; /* IE and Edge */
            scrollbar-width: none; /* Firefox */

            &::-webkit-scrollbar {
                display: none;
            }

            @include min-width(1024) {
                scroll-padding: 0;
            }

            // If you're wondering why this is commented
            // The reason is I didn't find any good reason for its existence
            // Whilst fixing another bug
            // If there's a particular reason for its existence, please replace this comment
            // &::after {
            //     content: '';
            //     padding-right: 20px;
            //     @include min-width(1024) {
            //         padding-right: 0;
            //     }
            // }

            .slide {
                position: relative;
                user-select: none;
                scroll-snap-align: start;
                flex: 1 0 100%;
                padding: 0 10px;

                @include min-width(640) {
                    flex: 1 0 50%;
                    scroll-snap-align: start;
                }

                @include min-width(1024) {
                    flex: 1 0 calc(33.33% + (2 * var(--container-padding) / 3));
                }
            }
        }
    }

    .button-round {
        position: absolute;
        z-index: 3;
        top: calc(50vw - 40px);

        @include min-width(640) {
            top: 25vw;
        }

        @include min-width(1024) {
            top: 160px;
        }

        &.disabled {
            display: none;
        }

        &.left {
            left: 5px;
        }

        &.right {
            right: 5px;
        }

        @include min-width(1280) {
            &.left {
                left: -20px;
            }

            &.right {
                right: -20px;
            }
        }
    }
}
</style>
